import { type Client, type UserCredentials, type User, createClient, createUser } from '@botpress/webchat-client'
import { nanoid } from 'nanoid'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  isFileType,
  type FileType,
  type BlockMessage,
  type IntegrationMessage,
  type WebchatError,
  type Feedback,
  MISSING_CLIENT_ID,
  INVALID_API_URL,
} from '../types'
import { createEventEmitter, EventEmitter } from '@bpinternal/shared'
import { getUseWebchatClientStore, StorageLocation, useMessagesStore, useParticipantStore } from '../stores'
import { integrationMessageToBlockMessage } from '../adapters'
import { type Events } from '../events'
import { useIsTyping } from './useIsTyping'
import { handleIntegrationError } from '../utils'
import { useAsync } from 'react-use'
import { getUseChatSoundStore } from '../stores/soundStore'
import { SOUND_EFFECTS } from '../assets/audio'

type UserResponse = Awaited<ReturnType<Client['getUser']>>['user']
type UserProfile = Omit<User, 'id' | 'createdAt' | 'updatedAt'>

type Props = {
  adminSecret?: string
  user?: UserCredentials
  conversationId?: string
  clientId: string
  botId?: string
  apiUrl?: string
  storageKey?: string
  storageLocation?: StorageLocation
  soundEnabled?: boolean
}

type ClientStates = 'connecting' | 'connected' | 'error' | 'disconnected'

type ScopedClient = {
  sendMessage: (payload: IntegrationMessage['payload']) => Promise<void>
  sendEvent: (event: Record<string, any>, options?: { bindConversation?: boolean; bindUser?: boolean }) => Promise<void>
  uploadFile: (file: File) => Promise<{ fileUrl: string; name: string; type: FileType }>
  addMessageFeedback: (messageId: string, feedback: Feedback) => Promise<void>
  getUser: () => Promise<UserResponse>
  updateUser: (user: UserProfile) => Promise<User>
}

export type UseWebchatReturn =
  | {
      clientState: 'connected'
      on: EventEmitter<Events>['on']
      client: ScopedClient
      messages: BlockMessage[]
      conversationId: string
      newConversation: () => Promise<void>
      isFetchingMessages: boolean
      user?: UserCredentials
      isTyping: boolean
      participants: User[]
      soundOn: boolean
      toggleSound: () => void
      error?: WebchatError
    }
  | {
      clientState: 'connecting' | 'error' | 'disconnected'
      on: EventEmitter<Events>['on']
      client: undefined
      messages: BlockMessage[]
      conversationId: undefined
      newConversation: () => Promise<void>
      isFetchingMessages: boolean
      user: undefined
      isTyping: undefined
      participants: User[]
      soundOn: boolean
      toggleSound: () => void
      error?: WebchatError
    }

export function useWebchat({
  apiUrl = 'https://webchat.botpress.cloud',
  clientId,
  storageKey,
  storageLocation,
  adminSecret,
  soundEnabled,
  ...props
}: Props): UseWebchatReturn {
  const useWebchatClientStore = getUseWebchatClientStore(storageKey, storageLocation)

  const storageConversationId = useWebchatClientStore((state) => state.conversationId)
  const storageUserCredentials = useWebchatClientStore((state) => state.user)
  const setStorageConversationId = useWebchatClientStore((state) => state.setConversationId)
  const setStorageUserCredentials = useWebchatClientStore((state) => state.setUser)

  const messages = useMessagesStore((state) => state.messages)
  const saveMessage = useMessagesStore((state) => state.saveMessage)
  const updateMessage = useMessagesStore((state) => state.updateMessage)
  const fetchMessages = useMessagesStore((state) => state.fetchMessages)
  const setMessages = useMessagesStore((state) => state.setMessages)
  const clearMessages = useMessagesStore((state) => state.clearMessages)
  const isFetchingMessages = useMessagesStore((state) => state.isFetching)

  const participants = useParticipantStore((state) => state.participants)
  const setParticipants = useParticipantStore((state) => state.setParticipants)
  const addParticipant = useParticipantStore((state) => state.addParticipant)
  const removeParticipant = useParticipantStore((state) => state.removeParticipant)
  const clearParticipants = useParticipantStore((state) => state.clearParticipants)

  const useChatSoundStore = getUseChatSoundStore(`webchat-sound-${clientId}`)
  const soundOn = useChatSoundStore((s) => s.soundOn)
  const toggleSound = useChatSoundStore((s) => s.toggleSound)

  const { isTyping, setIsTyping } = useIsTyping()
  const emitter = useMemo(() => createEventEmitter<Events>(), [])

  const [clientState, setClientState] = useState<ClientStates>('connecting')
  const [conversationId, setConversationId] = useState<string | undefined>(props.conversationId)
  const [userCredentials, setUserCredentials] = useState<UserCredentials | undefined>(props.user)
  const [httpClient, setHttpClient] = useState<Client | undefined>(undefined)
  const [error, setError] = useState<WebchatError | undefined>(undefined)

  const initializing = useRef(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const soundEnabledRef = useRef<boolean>(soundEnabled ?? false)
  useEffect(() => {
    soundEnabledRef.current = soundEnabled ?? false
  }, [soundEnabled])

  const soundOnRef = useRef<boolean>(soundOn)
  useEffect(() => {
    soundOnRef.current = soundOn
  }, [soundOn])

  const initialize = async () => {
    if (initializing.current) return
    initializing.current = true

    setError(undefined)
    setIsTyping(false)
    setClientState('connecting')

    try {
      const initialUserCredentials = props.user || storageUserCredentials
      let currentUserCredentials = initialUserCredentials

      if (!currentUserCredentials) {
        const { key, user } = await createUser({ clientId, apiUrl, adminSecret })
        currentUserCredentials = { userId: user.id, userToken: key }
      }

      const client = createClient({ userKey: currentUserCredentials.userToken, clientId, apiUrl })

      let currentConversationId = props.conversationId || storageConversationId

      if (!currentConversationId || !initialUserCredentials) {
        clearMessages()
        clearParticipants()
        const { conversation } = await client.createConversation()
        currentConversationId = conversation.id
      } else {
        const messages = await fetchMessages({ client: client, conversationId: currentConversationId })
        setMessages(messages)

        const { participants } = await client.listParticipants({ conversationId: currentConversationId })
        setParticipants(participants)
      }

      setUserCredentials(currentUserCredentials)
      setStorageUserCredentials(currentUserCredentials)

      setHttpClient(client)

      setConversationId(currentConversationId)
      setStorageConversationId(currentConversationId)

      audioRef.current = new Audio()
      audioRef.current.preload = 'auto'
      audioRef.current.src = SOUND_EFFECTS.chatMessageNotification
      audioRef.current.volume = 0.9

      setClientState('connected') // should we set ourselves to connected here ?
    } catch (error) {
      clearMessages()
      setError(handleIntegrationError(error))
      setClientState('error')
    } finally {
      initializing.current = false
    }
  }

  useAsync(async () => {
    const configError = checkConfiguration(clientId, apiUrl)
    if (configError) {
      setError(configError)
      setClientState('error')
      return
    }

    await initialize()
  }, [props.user?.userId, props.user?.userToken, props.conversationId])

  const restart = async () => {
    await initialize()
  }

  useEffect(() => {
    if (!httpClient || !conversationId) return

    const on = httpClient.listenConversation({ conversationId })

    const unsubscribeFunctions = [
      on('open', async () => {
        setError(undefined)

        const { event } = await httpClient.createEvent({
          conversationId,
          payload: {
            type: 'conversation_started',
            data: {},
          },
        })

        // The client gen lib has some trouble with additional properties : https://github.com/hey-api/openapi-ts/issues/2352
        if ((event.payload.data as Record<string, Record<string, string>>)?.status?.isInitial) {
          emitter.emit('conversation', conversationId)
        }

        setClientState('connected')
      }),
      on('error', (err) => {
        setError((prev) => prev || handleIntegrationError(err))
        setClientState('error')
        emitter.emit('error', new Error(`Connection to conversation lost: ${err?.message}`))
      }),
      on('message_created', (ev) => {
        const message = integrationMessageToBlockMessage(ev)

        if (userCredentials?.userId !== message.authorId) {
          if (soundEnabledRef.current && soundOnRef.current && audioRef.current) {
            audioRef.current.play().catch(() => {})
          }
          setIsTyping(false)
          emitter.emit('isTyping', { isTyping: false, timeout: 0 })
        }

        saveMessage(message)
        emitter.emit('message', message)
      }),
      on('message_status_changed', (ev) => {
        if (ev.newStatus === 'processing') {
          setIsTyping(true)
          emitter.emit('isTyping', { isTyping: true, timeout: 5000 })
          updateMessage(ev.message.metadata?.clientMessageId, { status: ev.newStatus })
        }
      }),
      on('participant_added', (ev) => {
        addParticipant(ev.participant)
        emitter.emit('participantAdded', ev.participant)
      }),
      on('participant_removed', (ev) => {
        removeParticipant(ev.participantId)
        emitter.emit('participantRemoved', ev.participantId as any) //TODO: Fix any
      }),
      on('webchat_visibility', (ev) => emitter.emit('webchatVisibility', ev.visibility)),
      on('webchat_config', (ev) => emitter.emit('webchatConfig', ev.config)),
      on('custom', (ev) => emitter.emit('customEvent', ev.event)),
    ]

    return () => {
      unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
    }
  }, [conversationId, httpClient])

  if (clientState !== 'connected' || !userCredentials || !conversationId || !httpClient) {
    return {
      clientState: clientState as 'connecting' | 'error' | 'disconnected',
      on: emitter.on,
      client: undefined,
      messages: [],
      conversationId: undefined,
      newConversation: restart,
      user: undefined,
      isTyping: undefined,
      isFetchingMessages,
      participants: [],
      toggleSound,
      soundOn,
      error,
    }
  }

  const client = {
    sendMessage: async (payload: IntegrationMessage['payload']) => {
      const temporaryId = nanoid()

      const message = {
        id: temporaryId,
        createdAt: new Date().toISOString(),
        userId: userCredentials.userId,
        conversationId,
        payload,
        metadata: {
          clientMessageId: temporaryId,
          delivered: false,
        },
      }

      const blockMessage = integrationMessageToBlockMessage(message)
      blockMessage.status = 'pending'
      saveMessage(blockMessage)

      await httpClient
        .createMessage({
          conversationId,
          payload,
          metadata: {
            clientMessageId: temporaryId,
          },
        })
        .catch((error) => emitter.emit('error', error))
    },
    sendEvent: async (event: Record<string, unknown>, options?: { bindConversation?: boolean; bindUser?: boolean }) => {
      await httpClient.createEvent({
        conversationId,
        payload: {
          type: 'custom',
          data: event,
        },
        bindConversation: options?.bindConversation ?? true,
        bindUser: options?.bindUser ?? true,
      })
    },
    uploadFile: async (file: File): Promise<{ fileUrl: string; name: string; type: FileType }> => {
      const { size, name } = file

      const getFileBuffer = (file: File): Promise<ArrayBuffer> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = (event) => resolve(event.target?.result as ArrayBuffer)
          reader.onerror = (event) => reject(event.target?.error)
          reader.readAsArrayBuffer(file)
        })
      }

      const buffer = await getFileBuffer(file)

      const thirtyDaysFromNow = new Date()
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30)

      const {
        file: { uploadUrl, contentType: mimeType, url: fileUrl },
      } = await httpClient.createFile({
        size,
        key: name,
        accessPolicies: ['public_content'],
        index: false,
        tags: { source: 'integration', integrationName: 'webchat' },
        expiresAt: thirtyDaysFromNow.toISOString(),
      })
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'x-amz-tagging': 'public=true',
          'content-type': mimeType,
        },
        body: buffer,
      })

      const contentType = mimeType.split('/').shift() ?? ''
      const fileType = isFileType(contentType) ? contentType : ('file' as const)
      return { fileUrl, name, type: fileType }
    },
    addMessageFeedback: async (messageId: string, feedback: Feedback) => {
      updateMessage(messageId, { feedback: feedback.value })

      await httpClient.addMessageFeedback({
        messageId,
        ...feedback,
      })
    },
    getUser: async () => {
      const { user } = await httpClient.getUser()
      return user
    },
    updateUser: async (user: UserProfile) => {
      const { user: updatedUser } = await httpClient.updateUser({
        name: user.name,
        pictureUrl: user.pictureUrl,
        userData: user.data,
      })

      return updatedUser
    },
  }

  return {
    clientState: 'connected',
    on: emitter.on,
    client,
    messages,
    conversationId,
    newConversation: async () => {
      try {
        clearMessages()
        clearParticipants()
        setIsTyping(false)
        const { conversation } = await httpClient.createConversation()
        setConversationId(conversation.id)
        setStorageConversationId(conversation.id)
      } catch (error) {
        setError(handleIntegrationError(error))
        setClientState('error')
      }
    },
    user: storageUserCredentials,
    isTyping,
    isFetchingMessages,
    participants,
    error,
    soundOn,
    toggleSound,
  }
}

const checkConfiguration = (clientId: string, apiUrl: string): WebchatError | undefined => {
  if (!clientId) {
    return { type: 'configuration', message: MISSING_CLIENT_ID }
  }
  if (!URL.canParse(apiUrl)) {
    return { type: 'configuration', message: INVALID_API_URL }
  }
}
